import React, { useEffect, useState } from 'react';
import { Archive, Trash2, ShieldCheck, Loader2, AlertTriangle, X, ArrowLeft, Lock } from 'lucide-react';
import { Button } from '../Button';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { holdIdleExit } from '../../services/idleHold';
import {
  fetchProjektDokumente, loescheAlleProjektDokumente,
} from '../../services/projektDokumenteService';
import {
  ARCHIV_BLEIBT_ERHALTEN, archivFrageText, erzeugteBleibenText, formatBytes, istErzeugteUnterlage,
  speicherGesamt, zaehleGesperrte,
} from './dokumenteLogic';
import { DokumenteArchivEntscheidung } from './types';

interface Props {
  projectId: string;
  /** Anzeigename des Entscheiders — er soll den Profilwechsel ueberleben. */
  benutzerName?: string;
  /**
   * Sieht der Entscheider gesperrte Dokumente — Admin oder (Rollensystem)
   * Recht „„Nur Admin“-Dokumente“ (darfAdminSchloss)? Der Name stammt aus der
   * Zeit vor den Rechten und bleibt, weil die Aufrufer ihn setzen. Steuert
   * nur EINEN Satz im Dialog.
   *
   * Wer das nicht darf, bekommt gesperrte Dokumente vom Server gar nicht erst
   * geliefert; sie werden von seinem Raeumen also nicht erfasst und bleiben
   * mit ihrer Datei stehen. Die genannte Zahl ist damit sein Ausschnitt und
   * nicht der Gesamtbestand — und das soll dastehen, statt dass er es fuer
   * eine vollstaendige Bilanz haelt.
   */
  istAdmin?: boolean;
  onAbbrechen: () => void;
  /**
   * Das Projekt wird abgeschlossen. `entscheidung` ist `null`, wenn es nichts
   * zu entscheiden gab (leere Ablage) oder wenn der Bestand nicht gelesen
   * werden konnte — dann ist auch nichts zu vermerken. Liegen NUR erzeugte
   * Unterlagen in der Ablage, gibt es trotzdem einen Vermerk (`keep: true`,
   * `count: 0`, `erzeugteBehalten: n`): die Ablage war nicht leer, und genau
   * das soll spaeter noch ablesbar sein.
   */
  onAbschliessen: (entscheidung: DokumenteArchivEntscheidung | null) => void;
}

/**
 * Die Rueckfrage beim Archivieren: „Dokumente aus der Ablage behalten?"
 *
 * Sie ersetzt das nackte `window.confirm`, das bisher vor dem Abschliessen
 * stand. Der Grund fuer die Frage ist Speicher: ein abgeschlossenes Projekt
 * zieht seine Plansaetze und Fotostrecken sonst jahrelang hinter sich her.
 *
 * ═══ DER ENTSCHEIDENDE SATZ IM DIALOG ════════════════════════════════════
 * Geloescht wird AUSSCHLIESSLICH die Handablage dieses Moduls. Begehungs-
 * berichte, SiGe-Plaene, die Unterlage fuer spaetere Arbeiten,
 * Vorankuendigungen, Einweisungsblaetter und Rechnungen liegen in anderen
 * Tabellen und anderen Buckets und werden hier nicht angefasst. Ohne diesen
 * Satz liest sich die Frage wie „soll ich das Projekt ausraeumen?" — und dann
 * klickt jeder auf „behalten", und die Frage haette man sich sparen koennen.
 *
 * Eine Ausnahme liegt IN der Ablage: die von der App abgelegten eigenen
 * Unterlagen — Fassungen aus Vorankuendigung, SiGe-Plan, A+S-Plan, UfsA,
 * Baustellenordnung, Handbuch, Einweisung (`erzeugt_aus`, Nr. 160/161; im
 * Altschema die A+S-Heuristik, siehe `istErzeugteUnterlage`). Sie sind
 * Nachweise, werden hier gar nicht erst mitgezaehlt, das Raeumen laesst sie
 * stehen (`erzeugteBehalten`), und der Dialog sagt, wie viele es sind.
 *
 * ═══ ZWEI SCHRITTE VOR DEM LOESCHEN ══════════════════════════════════════
 * Der Dialog erscheint auf dem NORMALWEG „Projektstatus → Archiv", also bei
 * Leuten, die eigentlich nur einen Status setzen wollten. Ein einziger Klick
 * duerfte dort nicht eine ganze Projektablage endgueltig vernichten —
 * ausgerechnet die harmlosere Einzelloeschung fragt sehr wohl nach. Deshalb
 * die zweite Ansicht: sie sagt, was gleich unwiederbringlich weg ist.
 *
 * ═══ REIHENFOLGE ═════════════════════════════════════════════════════════
 * Geloescht wird VOR dem Statuswechsel und nur bei Erfolg abgeschlossen: geht
 * das Loeschen schief, bleibt das Projekt offen und der Anwender sieht warum.
 * Andersherum waere das Projekt archiviert und die Ablage haengengeblieben,
 * ohne dass es noch jemandem auffiele.
 *
 * Der Preis dieser Reihenfolge ist bekannt und bewusst in Kauf genommen:
 * schlaegt danach das SPEICHERN des Statuswechsels fehl (Netz weg,
 * Warteschlange), sind die Dokumente weg und das Projekt noch offen. Das ist
 * der kleinere Schaden — der Anwender sieht ein offenes Projekt und kann es
 * erneut abschliessen, waehrend eine stumm liegengebliebene Ablage genau den
 * Speicher belegt, den dieser Dialog freigeben soll.
 */
export const ArchivDokumenteDialog: React.FC<Props> = ({
  projectId, benutzerName, istAdmin = false, onAbbrechen, onAbschliessen,
}) => {
  const [laden, setLaden] = useState(true);
  const [anzahl, setAnzahl] = useState(0);
  const [bytes, setBytes] = useState(0);
  /**
   * Wie viele der zu loeschenden Dokumente das Admin-Schloss tragen.
   *
   * Wer sie sieht, ist Admin — und ausgerechnet diese Unterlagen sind die, die
   * man am wenigsten versehentlich verlieren will (Vertraege, Schriftwechsel,
   * Aktenvermerke). Sie stumm mitzuloeschen waere der unangenehmste Fall
   * dieses Dialogs.
   *
   * Fuer alle anderen ist die Zahl zwangslaeufig 0: gesperrte Zeilen liefert
   * der Server ihnen gar nicht, sie werden vom Raeumen also auch nicht
   * erfasst und bleiben mit ihrer Datei stehen.
   */
  const [gesperrt, setGesperrt] = useState(0);
  /**
   * Wie viele von der App abgelegte eigene Unterlagen in der Ablage liegen.
   * Sie stehen nicht zur Wahl: `anzahl`, `bytes` und `gesperrt` zaehlen nur,
   * was tatsaechlich geloescht wuerde, und diese Zahl sagt, was daneben
   * stehen bleibt.
   */
  const [erzeugte, setErzeugte] = useState(0);
  /**
   * Der Bestand konnte NICHT gelesen werden — streng zu trennen von „es gibt
   * keine Dokumente". Ohne diese Unterscheidung behauptete der Dialog bei
   * jedem Netzaussetzer, die Ablage sei leer, und schriebe das anschliessend
   * auch noch als Tatsache ins Projekt.
   */
  const [ladefehler, setLadefehler] = useState<string | null>(null);
  const [loeschfehler, setLoeschfehler] = useState<string | null>(null);
  const [nachfrage, setNachfrage] = useState(false);
  const [arbeitet, setArbeitet] = useState(false);

  useEffect(() => {
    let abgebrochen = false;
    (async () => {
      try {
        const liste = await fetchProjektDokumente(projectId);
        if (abgebrochen) return;
        const loeschbar = liste.filter(d => !istErzeugteUnterlage(d));
        setAnzahl(loeschbar.length);
        setBytes(speicherGesamt(loeschbar));
        setGesperrt(zaehleGesperrte(loeschbar));
        setErzeugte(liste.length - loeschbar.length);
      } catch (e) {
        if (!abgebrochen) setLadefehler(rechtFehlerOder(e));
      } finally {
        if (!abgebrochen) setLaden(false);
      }
    })();
    return () => { abgebrochen = true; };
  }, [projectId]);

  /** Nichts zu entscheiden: leere Ablage oder Bestand unbekannt. */
  const ohneEntscheidung = ladefehler !== null || (anzahl === 0 && erzeugte === 0);

  /** Der Satz zu den behaltenen Fassungen — `null`, wenn es keine gibt. */
  const erzeugteSatz = erzeugteBleibenText(erzeugte);

  const behalten = () => {
    onAbschliessen(ohneEntscheidung
      ? null
      : {
        decidedAt: new Date().toISOString(),
        keep: true,
        count: anzahl,
        bytes,
        decidedByName: benutzerName,
        ...(erzeugte > 0 ? { erzeugteBehalten: erzeugte } : {}),
      });
  };

  const loeschen = async () => {
    setArbeitet(true);
    setLoeschfehler(null);
    const freigeben = holdIdleExit('Dokumentenablage wird geräumt');
    try {
      const ergebnis = await loescheAlleProjektDokumente(projectId, { erzeugteBehalten: true });
      onAbschliessen({
        decidedAt: new Date().toISOString(),
        keep: false,
        count: ergebnis.anzahl,
        bytes: ergebnis.bytes,
        decidedByName: benutzerName,
        ...(ergebnis.behalten > 0 ? { erzeugteBehalten: ergebnis.behalten } : {}),
      });
    } catch (e) {
      setLoeschfehler(rechtFehlerOder(e));
      setArbeitet(false);
      setNachfrage(false);
    } finally {
      freigeben();
    }
  };

  return (
    <div className="fixed inset-0 z-[160] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ${nachfrage ? 'bg-rose-50 text-rose-600' : 'bg-slate-100 text-slate-600'}`}>
              {nachfrage ? <Trash2 className="w-5 h-5" /> : <Archive className="w-5 h-5" />}
            </div>
            <div>
              <h3 className="font-bold text-slate-800">
                {nachfrage ? 'Dokumentenablage endgültig löschen?' : 'Projekt abschließen'}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {nachfrage ? 'Dieser Schritt lässt sich nicht rückgängig machen.' : 'Das Projekt wandert ins Archiv.'}
              </p>
            </div>
          </div>
          <button onClick={onAbbrechen} disabled={arbeitet} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          {laden ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-blue-500" /></div>
          ) : nachfrage ? (
            /* Zweiter Schritt: nur noch die Folge, kein neuer Sachverhalt. */
            <>
              <div className="p-3 rounded-xl bg-rose-50 border border-rose-200">
                <p className="text-sm text-rose-800">
                  <strong>{anzahl === 1 ? '1 Dokument' : `${anzahl} Dokumente`}</strong> ({formatBytes(bytes)}) werden
                  aus der Ablage entfernt — Datei und Eintrag. Es gibt keinen Papierkorb und keine Wiederherstellung.
                </p>
              </div>
              {gesperrt > 0 && (
                <div className="p-3 rounded-xl bg-indigo-50 border border-indigo-200 flex items-start gap-2">
                  <Lock className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-indigo-900">
                    <strong>{gesperrt === 1 ? 'Ein Dokument trägt' : `${gesperrt} Dokumente tragen`} das
                    Admin-Schloss</strong> und {gesperrt === 1 ? 'wird' : 'werden'} mitgelöscht. Solche Unterlagen
                    liegen hier, weil sie vertraulich sind — bitte kurz prüfen, ob sie wirklich weg sollen.
                  </p>
                </div>
              )}

              <p className="text-sm text-slate-600">
                Alles, was die Anwendung selbst erzeugt hat, bleibt unberührt — Begehungsberichte, SiGe-Pläne,
                Unterlage, Vorankündigungen, Einweisungsblätter und Rechnungen in ihren Modulen.
                {erzeugteSatz && <> Auch in der Ablage selbst: <strong>{erzeugteSatz}</strong></>}
              </p>
            </>
          ) : (
            <>
              {ladefehler ? (
                <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold">Die Dokumentenablage ließ sich nicht lesen.</p>
                    <p className="mt-1 whitespace-pre-line">{ladefehler}</p>
                    <p className="mt-1.5">
                      Ob dieses Projekt Dokumente hat, ist damit offen — es wird nichts gelöscht.
                      Das Projekt lässt sich trotzdem abschließen; die Ablage kann später geräumt werden.
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-700">{archivFrageText(anzahl, bytes, erzeugte)}</p>
              )}

              {/* Bei `anzahl === 0` steht der Satz schon in `archivFrageText`. */}
              {!ladefehler && anzahl > 0 && erzeugteSatz && (
                <p className="text-sm text-slate-700">
                  Nicht zur Wahl stehen die eigenen Unterlagen, die die App als Nachweis abgelegt hat:{' '}
                  <strong>{erzeugteSatz}</strong>
                </p>
              )}

              {!ladefehler && !istAdmin && anzahl > 0 && (
                <p className="text-[11px] text-slate-400">
                  Gezählt ist, was Sie sehen dürfen. Dokumente mit Admin-Schloss bleiben in jedem Fall erhalten.
                </p>
              )}

              {!ladefehler && anzahl > 0 && (
                <>
                  <p className="text-sm text-slate-600">
                    Sollen diese Dokumente erhalten bleiben? Wer sie nicht mehr braucht, gibt mit dem Löschen
                    <strong> {formatBytes(bytes)}</strong> Speicher frei.
                  </p>

                  <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                    <div className="flex items-start gap-2">
                      <ShieldCheck className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                      <div className="text-xs text-emerald-900">
                        <p className="font-bold mb-1">Davon bleibt in jedem Fall alles erhalten:</p>
                        <ul className="space-y-0.5">
                          {ARCHIV_BLEIBT_ERHALTEN.map(p => <li key={p}>• {p}</li>)}
                        </ul>
                        <p className="mt-1.5">Gelöscht wird ausschließlich die Dokumentenablage dieses Projekts.</p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {loeschfehler && (
                <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-sm text-rose-700 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold">Das Räumen ist fehlgeschlagen — das Projekt bleibt offen.</p>
                    <p className="mt-1 whitespace-pre-line">{loeschfehler}</p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-200 bg-slate-50 flex flex-wrap items-center justify-end gap-2 shrink-0">
          {nachfrage ? (
            <>
              <Button variant="ghost" onClick={() => setNachfrage(false)} disabled={arbeitet} icon={<ArrowLeft className="w-4 h-4" />}>
                Zurück
              </Button>
              <Button
                variant="danger" onClick={() => void loeschen()} disabled={arbeitet}
                icon={arbeitet ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              >
                {arbeitet ? 'Wird geräumt…' : 'Endgültig löschen'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={onAbbrechen} disabled={arbeitet}>Abbrechen</Button>
              {!laden && !ladefehler && anzahl > 0 && (
                <Button variant="danger" onClick={() => setNachfrage(true)} icon={<Trash2 className="w-4 h-4" />}>
                  {`Löschen (${formatBytes(bytes)} frei)`}
                </Button>
              )}
              <Button onClick={behalten} disabled={laden} icon={<Archive className="w-4 h-4" />}>
                {!ladefehler && anzahl > 0 ? 'Dokumente behalten' : 'Abschließen'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
